import {
  ClipboardListIcon,
  FileMinusIcon,
  FileTextIcon,
  LayoutDashboardIcon,
  ListChecksIcon,
  MailIcon,
  ReceiptTextIcon,
  ScanLineIcon,
  SearchIcon,
  SettingsIcon,
  TruckIcon,
  UsersIcon,
} from "lucide-react"
import type * as React from "react"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation } from "react-router-dom"
import type { Capability } from "@steelbill/types"
import { NavMain } from "@/components/nav-main"
import { NavSecondary } from "@/components/nav-secondary"
import { NavUser } from "@/components/nav-user"
import { CommandShortcut } from "@/components/ui/command"
import { ScanAction } from "@/features/scan/ScanAction"
import { GlobalSearchButton } from "@/features/search/GlobalSearch"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useAuth } from "@/features/auth/auth-context"
import { SETTINGS_AREA_CAPABILITIES } from "@/features/settings/settings-sections"
import { HOME_SCREEN_CAPABILITIES, landingPath } from "@/routes/landing"
import { routes } from "@/routes/paths"

/**
 * The sidebar — `dashboard-01`'s, with SteelBill's destinations.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERY ITEM IS CAPABILITY-GATED (§2.1), never role-gated. `can()` is the same
 * function `requireCap` uses on the server, imported from `packages/types`, so
 * the nav and the API cannot disagree about who may go where. A nav item the
 * user cannot act on is worse than a missing one: it turns a permission
 * boundary into a 403 discovered only after the tap.
 *
 * That is why SETTINGS is not gated on `settings.view`. Every role holds it, and
 * everything behind the row — company details, tax master data, accounts, the
 * audit trail — needs a write or an administration capability. Showing the owner
 * a door he cannot open is worse than not showing it. What the row IS gated on
 * is `SETTINGS_AREA_CAPABILITIES`, which is three capabilities and not one; the
 * `secondary` block below says why at length.
 * ─────────────────────────────────────────────────────────────────────────────
 */

interface NavItem {
  titleKey: string
  url: string
  icon: React.ReactNode
  /**
   * What the reader must hold for this row to exist.
   *
   * A LIST MEANS "ANY ONE OF THESE", and exactly two rows need it: HOME and
   * SETTINGS. Every other destination is one screen behind one capability, and a
   * list there would be a way of quietly widening a gate — so the plain
   * `Capability` form is kept and is what everything else uses.
   *
   * Neither exception is one screen behind one capability. Home is §11.2's four
   * action tiles, worth showing to anyone who can start ANY document; Settings
   * is an AREA of five sections, worth showing to anyone who can open ANY of
   * them. In both cases the list is owned by the module that also resolves where
   * the row LANDS — `routes/landing.ts` for Home,
   * `features/settings/settings-sections.ts` for Settings — so the rail and the
   * router cannot disagree about whether the destination is yours.
   */
  capability: Capability | readonly Capability[]
  /** Match this path exactly. Only Home needs it — `"/"` prefix-matches all. */
  end?: boolean
}

/**
 * What Quick Create offers. Each is capability-gated like everything else, and
 * if a role holds none of them the button is not rendered at all rather than
 * opening an empty menu.
 */
interface CreateAction {
  titleKey: string
  url: string
  capability: Capability
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation()
  const { can, user, signOut } = useAuth()
  const { pathname } = useLocation()
  const { setOpenMobile } = useSidebar()

  /*
   * ON A PHONE THE SIDEBAR IS A SHEET, AND NOTHING EVER CLOSED IT.
   *
   * Tapping "Bills" navigated correctly and then left the drawer sitting on top
   * of the screen it had just opened — the app looked frozen, and the only way
   * out was the backdrop. It affected every nav item and the scanner from the
   * day the shell was built; it went unnoticed because a desktop browser is
   * never in this mode.
   *
   * TWO MECHANISMS, BECAUSE ONE OF THEM HAS A HOLE. This effect catches
   * navigation from ANYWHERE — the palette opened with ⌘K on a tablet keyboard,
   * a redirect, a `navigate()` deep in a screen — none of which is a click in
   * here. What it CANNOT catch is tapping the item for the route you are
   * already on: the pathname does not change, the effect does not re-run, and
   * the drawer just sits there looking like the tap missed. That case is the
   * `closeDrawerOnActivate` handler below, which fires on the interaction
   * rather than on its result. Neither one alone is enough; together they have
   * no gap. On a desktop `openMobile` is already false, so both are no-ops
   * there rather than something that would collapse the rail on every click.
   */
  useEffect(() => {
    setOpenMobile(false)
  }, [pathname, setOpenMobile])

  /**
   * Close the drawer when something in it is activated.
   *
   * Delegated rather than wired onto each item, so a nav entry added next year
   * cannot forget to opt in — the same argument `site-header` makes for reading
   * the location instead of having every screen push a title up.
   *
   * IT IS ATTACHED TO THE HEADER AND THE CONTENT, NOT TO `<Sidebar>`, and that
   * is not a style choice. On a phone `Sidebar` renders a `<Sheet>` and spreads
   * its props onto the Base UI dialog ROOT, which is not a DOM element — an
   * `onClick` handed to it is silently dropped. Put there, this fixed nothing
   * and the tests said so. `SidebarHeader` and `SidebarContent` are real divs.
   *
   * ANCHORED POPUPS ARE THE ONE EXCEPTION, and getting this wrong broke Quick
   * Create: its menu is positioned AGAINST the button you just pressed, so
   * closing the drawer unmounts the anchor mid-open and the menu never appears.
   * `aria-haspopup` is precisely the "pressing me opens a popup attached to me"
   * signal, which makes it the right discriminator rather than a list of
   * component names that would go stale. The profile menu at the foot carries
   * it too, so the rule holds there even though the footer has no handler.
   *
   * Everything else in here either navigates away or opens a FULL-SCREEN
   * overlay, and both are better off with the drawer already gone — which is
   * also why tapping Search or Scan gives you one overlay rather than a palette
   * stacked on a drawer, two focus traps deep.
   */
  const closeDrawerOnActivate = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null
    if (target === null) return
    if (target.closest("a[href], button") === null) return
    if (target.closest("[aria-haspopup]") !== null) return
    setOpenMobile(false)
  }

  const main: NavItem[] = [
    /*
     * HOME, AND IT IS NOT OFFERED TO EVERYONE ANY MORE.
     *
     * ═══════════════════════════════════════════════════════════════════════
     * IT USED TO BE GATED ON `report.view`, WHICH WAS ALWAYS THE WRONG
     * QUESTION and became a visible defect the day the ACCOUNTANT could sign
     * in. Home has no capability of its own — it is a hub — so the gate was
     * picked as "something every role holds", and `report.view` is in the
     * shared read row. She holds it. So the rail offered her a row leading to
     * §11.2's making screen, where three of the four tiles are things she
     * cannot do and the fourth is a register that is already in the rail
     * below.
     *
     * WORSE, `/` NOW REDIRECTS HER TO REPORTS (see `routes/index.tsx`), so
     * that row would have followed to a screen it does not name and lit up a
     * DIFFERENT row when it got there. A nav item that lies about its
     * destination teaches the reader that the highlight cannot be trusted,
     * which costs more than the missing row.
     *
     * GATED ON THE SAME LIST THE ROUTER USES, so "Home is yours" is decided
     * once. Nothing here reads a role (§2.1): OWNER and ADMIN hold
     * `invoice.create` and see this row exactly as before.
     * ═══════════════════════════════════════════════════════════════════════
     */
    {
      titleKey: "nav.home",
      url: routes.home,
      icon: <LayoutDashboardIcon />,
      capability: HOME_SCREEN_CAPABILITIES,
      // `"/"` prefix-matches every route, so without this Home stays lit on
      // every screen and the rail never says where you are.
      end: true,
    },
    {
      titleKey: "nav.invoices",
      url: routes.invoices,
      icon: <ReceiptTextIcon />,
      capability: "invoice.view",
    },
    {
      titleKey: "nav.quotations",
      url: routes.quotations,
      icon: <FileTextIcon />,
      capability: "quotation.view",
    },
    /*
     * CHALLANS ARE BUILT — register, editor, detail screen, PDF and drafts, all
     * shipped 2026-08-15/16 (§17.7, pulled forward from post-v1 as a live Rule
     * 55 gap). This comment claimed the opposite until 2026-08-16.
     *
     * GATED ON THE CAPABILITY ONLY, AND THAT IS CORRECT, not an omission. §18.3
     * puts the flag check on the SERVER, before `requireCap`, answering 404
     * rather than 403 — and a client that asked "is the flag on?" would need
     * `GET /flags`, which requires `settings.write`, which the OWNER does not
     * hold. So the register itself renders the "not switched on" state when the
     * API 404s (`ChallansNotSwitchedOn` in ChallanListScreen.tsx), exactly as
     * the search palette lets its challan request fail quietly.
     */
    {
      titleKey: "nav.challans",
      url: routes.challans,
      icon: <TruckIcon />,
      capability: "challan.view",
    },
    /*
     * §17.3 — CREDIT NOTES. The fourth document register, and it belongs in the
     * rail even though nothing is CREATED from here.
     *
     * A credit note is raised from the bill it corrects (rule 1), so this is a
     * READING destination: "show me every reduction I have issued" is a real
     * question — the accountant asks it every month when she files the CDNR
     * section of GSTR-1 — and the register is the only place it is answered. It
     * sits after challans because it is the least often opened of the four.
     *
     * GATED ON THE CAPABILITY ONLY, exactly as the challan item above is, and
     * for the same reason: §18.3 puts the flag check on the SERVER, and a client
     * that asked "is the flag on?" would need `GET /flags`, which requires
     * `settings.write`, which the OWNER does not hold. THE FLAG SHIPS DISABLED,
     * so today this item leads to the register's own "not switched on" state —
     * which is the honest thing for a nav item to do and is exactly what the
     * challan register did through its own flag period.
     */
    {
      titleKey: "nav.creditNotes",
      url: routes.creditNotes,
      icon: <FileMinusIcon />,
      capability: "creditnote.view",
    },
    {
      titleKey: "nav.customers",
      url: routes.customers,
      icon: <UsersIcon />,
      capability: "customer.view",
    },
    { titleKey: "nav.items", url: routes.items, icon: <ListChecksIcon />, capability: "item.view" },
    {
      titleKey: "nav.reports",
      url: routes.reports,
      icon: <ClipboardListIcon />,
      capability: "report.view",
    },
  ]

  /** The foot of the rail: settings and search, as the block arranges it. */
  const secondary: NavItem[] = [
    /*
     * §17A.1 — THE MAILBOX, and it is down here with the tools rather than up in
     * `main` with the registers.
     *
     * `main` is the handful of things the owner touches daily (§11.2) — the four
     * document registers, customers, the rate card, reports. Mail is not one of
     * them: it is where he goes to CHECK something ("did that quotation actually
     * go, and to which address?"), which is the same kind of errand as Search and
     * Scan directly above and Settings directly below. It sits before Settings
     * because he opens it and does not open that one at all.
     *
     * IT IS A ROW AT ALL, WHERE THE NOTIFICATION HUB AND THE CONVERSATION ARE
     * NOT, and the difference is argued on `routes.mail`: those two are places a
     * BADGE sends you, and a permanent row reading "Messages: 0" is what the eye
     * learns to skip. Nobody is ever prompted towards the mailbox, so without a
     * door it does not exist.
     *
     * GATED ON `mail.view`, THE SAME CAPABILITY `routes/index.tsx` PUTS ON THE
     * SCREEN — never on a role, and never on `mail.send`. The ACCOUNTANT holds
     * neither (`permissions.ts` — READ_MAIL), so this is one of the few rows in
     * the rail that genuinely disappears for somebody, which is what makes it a
     * gate rather than a decoration.
     *
     * NO FLAG CHECK, exactly as challans and credit notes above have none: §18.2
     * puts the flag on the SERVER, and a client asking `GET /flags` would need
     * `settings.write`, which the OWNER does not hold. While the `mail` flag is
     * off this row leads to the screen's own "not switched on" state, which is
     * the honest thing for a nav item to do.
     */
    {
      titleKey: "nav.mail",
      url: routes.mail,
      icon: <MailIcon />,
      capability: "mail.view",
    },
    /*
     * ONE SETTINGS ROW, WHERE THERE WERE FOUR — Settings, Features, People and
     * Activity log. The destinations did not go anywhere: they are the
     * navigation menu across the top of the settings area
     * (`features/settings/SettingsLayout.tsx`), each still its own URL behind
     * its own gate. What changed is that the least-opened part of the app has
     * stopped taking four of the son's rows.
     *
     * ═══════════════════════════════════════════════════════════════════════
     * THE GATE IS A LIST OF THREE, AND GATING IT ON `settings.write` ALONE
     * WOULD BE THE MISTAKE §6.1'S COUNTER OVERRIDE COST AN AFTERNOON, WEARING A
     * NEW COSTUME. §2.1's Administration row lists `user.manage` and
     * `audit.view` as capabilities OF THEIR OWN; they coincide with
     * `settings.write` only because ADMIN happens to hold all three today. A
     * future role granted `user.manage` alone holds the People route's gate and
     * would have been shown no door to it — invisible but reachable, which is
     * the same class of failure as visible but forbidden and harder to spot.
     *
     * SO THE ROW IS OFFERED FOR ANY ONE OF THE THREE, AND `/settings` LANDS ON
     * THE FIRST SECTION THE READER CAN ACTUALLY OPEN (`SettingsIndexRoute` in
     * `routes/index.tsx`). Somebody holding only `user.manage` arrives at
     * People, never at a company form that would only bounce him Home. The list
     * and the landing come from ONE module, so the rail and the router cannot
     * disagree — the same construction `landing.ts` uses for Home.
     *
     * §6.1'S COUNTERS ARE A SECTION OF THAT MENU AND ARE DELIBERATELY NOT IN
     * THE GATE. `invoice.set_counter` is the OWNER's, his screen lives at
     * `/invoices/numbers` because that is what it is to him — the number the
     * next bill will get — and he reaches it from the register and the palette
     * as before. Putting it here would offer an ADMINISTRATION row to the one
     * person §2 keeps out of settings, leading to a menu of one item.
     *
     * IT IS HERE RATHER THAN IN `main` for the reason the four were: the rail's
     * main group is the handful of things the owner touches daily (§11.2), and
     * he holds none of these three, so he never sees this row at all. The foot
     * of the rail is where the son's administration lives.
     * ═══════════════════════════════════════════════════════════════════════
     */
    {
      titleKey: "nav.settings",
      url: routes.settings,
      icon: <SettingsIcon />,
      capability: SETTINGS_AREA_CAPABILITIES,
    },
  ]

  /**
   * Quick Create's menu — the things worth starting from anywhere.
   *
   * ═════════════════════════════════════════════════════════════════════════
   * THE CHALLAN WAS MISSING UNTIL 2026-08-16, on the highest-traffic control in
   * the app. The palette's own "Create" list (features/search/GlobalSearch.tsx)
   * already offered `btn.newChallan`, and `routes.challanNew` has been mounted
   * since §17.7 shipped — two hand-kept "things you can create" lists, one
   * updated. So the big plum "+" on every screen could not start the one
   * document that has to exist BEFORE THE LORRY LEAVES, and the owner went
   * Challans → New Challan every time.
   *
   * IT IS ORDERED BY URGENCY AT THE MOMENT OF TAPPING, not by importance: a
   * challan is written with a driver waiting at the gate, which is a worse place
   * to be two taps deep than the office is. It sits after the bill and the
   * quotation because those are the daily work.
   *
   * A TEST DERIVES THIS LIST FROM `routes`, rather than pinning a count — see
   * components/__tests__/app-sidebar.test.tsx. A count pin would have been
   * bumped from 4 to 5 by hand, which is the same hand-kept list one level up.
   * ═════════════════════════════════════════════════════════════════════════
   */
  const createActions: CreateAction[] = [
    { titleKey: "btn.newInvoice", url: routes.invoiceNew, capability: "invoice.create" },
    { titleKey: "btn.newQuotation", url: routes.quotationNew, capability: "quotation.create" },
    { titleKey: "btn.newChallan", url: routes.challanNew, capability: "challan.create" },
    { titleKey: "btn.addNewCustomer", url: routes.customerNew, capability: "customer.create" },
    { titleKey: "btn.addNewItem", url: routes.itemNew, capability: "item.create" },
  ]

  /** Deny by default: an empty list matches nobody, exactly as `can()` would. */
  const holds = (capability: Capability | readonly Capability[]): boolean =>
    typeof capability === "string" ? can(capability) : capability.some((c) => can(c))

  const visible = (items: NavItem[]) =>
    items
      .filter((item) => holds(item.capability))
      .map((item) => ({
        title: t(item.titleKey),
        url: item.url,
        icon: item.icon,
        end: item.end,
      }))

  /*
   * THE SCREEN THIS PERSON'S APP BEGINS ON LEADS THE RAIL.
   *
   * ═════════════════════════════════════════════════════════════════════════
   * For the OWNER and the son this changes NOTHING AT ALL: their landing is
   * Home, Home is already the first entry in `main`, and the sort finds it
   * where it is. The whole effect is on somebody whose landing is not Home.
   *
   * The ACCOUNTANT lands on REPORTS (`routes/landing.ts`). Left in declaration
   * order, Reports would be the LAST of her seven rows — so the screen she
   * opens the app on, and the reason she has a login, would sit at the bottom
   * of the rail under five registers, with the top of her nav occupied by the
   * ones she reads least. The rail would be describing somebody else's day.
   *
   * DERIVED FROM `landingPath`, NOT HAND-ORDERED PER ROLE. A second ordered
   * list keyed by role is precisely the branch §2.1 forbids, and it would go
   * stale the first time an item was renamed. One rule decides where `/` sends
   * you and which row leads; they cannot disagree.
   * ═════════════════════════════════════════════════════════════════════════
   */
  const landing = landingPath(can)
  const mainInOrder = [
    ...main.filter((item) => item.url === landing),
    ...main.filter((item) => item.url !== landing),
  ]

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader onClick={closeDrawerOnActivate}>
        <SidebarMenu>
          <SidebarMenuItem>
            {/*
              * THE LETTERHEAD GOES WHERE THE APP BEGINS, which is `/` for the
              * two people who write documents and `/reports` for the one who
              * files them. Pointing it at `routes.home` unconditionally would
              * send her through a redirect on every tap — a visible bounce, for
              * no reason, on the most-tapped control in the shell.
              */}
            <SidebarMenuButton
              className="data-[slot=sidebar-menu-button]:!p-1.5"
              render={<Link to={landing} />}
            >
              {/*
                * The letterhead plum (§12.4), and the only place any colour
                * appears in the whole shell. A mark, not a tinted surface.
                */}
              <span
                aria-hidden="true"
                className="flex size-5 shrink-0 items-center justify-center rounded bg-brand text-[0.625rem] font-bold text-brand-foreground"
              >
                {[...t("app.name")][0] ?? ""}
              </span>
              <span className="text-base font-semibold">{t("app.name")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent onClick={closeDrawerOnActivate}>
        <NavMain
          items={visible(mainInOrder)}
          quickCreate={createActions
            .filter((action) => can(action.capability))
            .map((action) => ({ title: t(action.titleKey), url: action.url }))}
        />
        {/*
          * THE TWO WAYS OF FINDING SOMETHING, TOGETHER.
          *
          * These are a pair and are placed as one: type what you remember, or
          * scan what you are holding. Splitting them — search in the header,
          * scan down here — meant the two halves of the same question lived on
          * opposite sides of the screen, and the sidebar's own magnifying glass
          * was the thing people reached for first anyway.
          *
          * Search sits ABOVE scan because it is the one that always works: the
          * scanner needs a printed barcode and a handheld reader, while typing
          * needs nothing. The rarer tool goes second.
          *
          * SCAN REPLACED A "SEARCH" NAV ITEM HERE. The old one pointed at
          * `routes.invoices` — a second link to the register wearing a
          * magnifying glass, which is the kind of nav item that teaches someone
          * the icons lie. What sits here now is the real thing.
          *
          * NEITHER IS CAPABILITY-GATED, because finding a document is not a
          * privilege — and both are gated where it counts: the palette filters
          * its own rows through `can()`, and a scan lands on a route that
          * enforces its own.
          */}
        <div className="mt-auto px-2 pb-1">
          <SidebarMenu>
            <SidebarMenuItem>
              <GlobalSearchButton
                render={(open) => (
                  <SidebarMenuButton
                    tooltip={t("search.title")}
                    onClick={open}
                    /*
                     * The shortcut is announced as a SHORTCUT, not as part of
                     * the name. Without this pair the button's accessible name
                     * computes to "Search ⌘K" — a screen reader reads the glyph
                     * as text, and "command K" lands as if it were part of what
                     * the button is called. `aria-keyshortcuts` is the attribute
                     * that exists for exactly this, and it names both bindings
                     * because the handler accepts either.
                     */
                    aria-keyshortcuts="Meta+K Control+K"
                  >
                    <SearchIcon />
                    <span>{t("nav.search")}</span>
                    {/*
                      * The hint is worth SHOWING because the shortcut is the
                      * fast path and nothing else advertises it. It hides in
                      * icon mode, where the label is gone and a bare "⌘K" beside
                      * an icon reads as a badge rather than a shortcut — inert
                      * today, since this sidebar is `offcanvas` and never
                      * collapses to a rail, but the header already carries the
                      * same defensive pairing and one mode switch should not
                      * have to find both.
                      */}
                    <CommandShortcut
                      aria-hidden="true"
                      className="ml-auto group-data-[collapsible=icon]:hidden"
                    >
                      ⌘K
                    </CommandShortcut>
                  </SidebarMenuButton>
                )}
              />
            </SidebarMenuItem>
            <SidebarMenuItem>
              <ScanAction
                render={(open) => (
                  <SidebarMenuButton tooltip={t("scan.action")} onClick={open}>
                    <ScanLineIcon />
                    <span>{t("scan.action")}</span>
                  </SidebarMenuButton>
                )}
              />
            </SidebarMenuItem>
          </SidebarMenu>
        </div>
        <NavSecondary items={visible(secondary)} />
      </SidebarContent>

      <SidebarFooter>
        <NavUser
          user={{
            name: user?.displayName ?? "",
            /*
             * `AuthUser` carries id, role and displayName — no email. The admin
             * signs in WITH one, but the token payload does not return it, so
             * there is nothing to show here without widening `AuthUserSchema`
             * and the three `/auth/*` responses. The role is what the session
             * actually knows, and it is the more useful line anyway: it says
             * which of the two accounts you are in.
             */
            email: user === null ? "" : t(`role.${user.role.toLowerCase()}`),
            avatar: "",
          }}
          onSignOut={signOut}
        />
      </SidebarFooter>
    </Sidebar>
  )
}
